/**
 * Per-tab cache for small, cheap-to-refetch client data (unread badge count,
 * saved-article list, guide reference metadata). Every call degrades to "no
 * cache" on the server, in private mode, on quota errors and on corrupt
 * entries, so callers never need their own try/catch.
 *
 * Only ever read inside a `useEffect`: several pages that render these
 * consumers are SSR'd, and seeding a `useState` initializer from storage would
 * make the server and client render different HTML.
 */
export type SessionCacheEntry<T> = { at: number; value: T }

export const readSessionCache = <T>(key: string): SessionCacheEntry<T> | undefined => {
  if (typeof window === 'undefined') return undefined
  try {
    const raw = window.sessionStorage.getItem(key)
    if (!raw) return undefined
    const entry = JSON.parse(raw)
    if (!entry || typeof entry.at !== 'number' || !('value' in entry)) return undefined
    return entry as SessionCacheEntry<T>
  } catch {
    return undefined
  }
}

/** The cached value while it is younger than `maxAgeMs`, else undefined. */
export const readFreshSessionCache = <T>(key: string, maxAgeMs: number): T | undefined => {
  const entry = readSessionCache<T>(key)
  return entry && Date.now() - entry.at < maxAgeMs ? entry.value : undefined
}

export const writeSessionCache = (key: string, value: unknown) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.setItem(key, JSON.stringify({ at: Date.now(), value }))
  } catch {
    // quota exceeded / storage disabled — the next read simply misses
  }
}

export const removeSessionCache = (key: string) => {
  if (typeof window === 'undefined') return
  try {
    window.sessionStorage.removeItem(key)
  } catch {}
}
